"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LARGE_PASTE_EVENT } from "@/components/tailwind/plugins/boardDeletionGuard";

/**
 * Global host for the large-paste notice.
 *
 * When a paste exceeds the size limit, the editor's paste handler
 * (boardDeletionGuard) cancels the inline paste and dispatches
 * `LARGE_PASTE_EVENT` on `window`. This component (mounted once in
 * providers.tsx) shows an informational dialog telling the user the paste was
 * too large and to import instead. It does NOT run the import itself — the user
 * imports via the normal import flow (sidebar / page menu).
 */
export default function LargePasteImportModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onLargePaste = () => setOpen(true);
    window.addEventListener(LARGE_PASTE_EVENT, onLargePaste);
    return () => window.removeEventListener(LARGE_PASTE_EVENT, onLargePaste);
  }, []);

  if (!open) return null;

  const close = () => setOpen(false);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      onClick={close}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="large-paste-title"
    >
      <div
        className="w-full max-w-[360px] rounded-2xl border border-zinc-200 bg-white p-6 text-center shadow-2xl dark:border-white/10 dark:bg-[#262626]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="large-paste-title"
          className="text-[15px] font-semibold leading-relaxed text-zinc-900 dark:text-zinc-50"
        >
          Sorry, that request was too large. Try import instead?
        </h2>

        <div className="mt-5">
          <button
            type="button"
            onClick={close}
            autoFocus
            className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm font-medium text-zinc-600 transition-colors hover-component dark:border-white/10 dark:text-zinc-300 "
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
